/**
 * The conversation.
 *
 * From docs/references/12-desktop-three-pane.png: header with the person,
 * a patterned wallpaper behind the thread, date dividers, bubbles that take
 * their side from who sent them, time and ticks tucked into the corner, and
 * the composer across the bottom.
 *
 * Two details that look cosmetic and are not:
 *
 *   Consecutive messages from one person lose their tail and tighten up.
 *   Without that a conversation reads as a list of unrelated cards.
 *
 *   The time sits inside the bubble, with padding reserved for it on the
 *   last line. Floating it outside is what causes the ragged right edge you
 *   see in chat apps that got this wrong.
 */

import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMessages, useChats, useAuth } from '../hooks/index.js'
import {
  formatTime, formatDayDivider, isSameDay, segments,
  media, userMessage, kindOf, isWithinLimit, MAX_UPLOAD_BYTES, formatSize
} from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import PhoneSheet from '../components/chat/PhoneSheet.jsx'
import Lightbox from '../components/chat/Lightbox.jsx'
import EmojiPicker from '../components/chat/EmojiPicker.jsx'
import MessageMedia from '../components/chat/MessageMedia.jsx'
import CameraSheet from '../components/chat/CameraSheet.jsx'
import './ChatRoom.css'

function Ticks ({ status }) {
  const read = status === 'read'
  return (
    <svg className={`cr__ticks${read ? ' is-read' : ''}`} viewBox="0 0 20 14" fill="none"
      aria-label={read ? 'Read' : 'Sent'}>
      <path d="M1 7.6 4.4 11 11.2 3.4" stroke="currentColor" strokeWidth="1.7"
        strokeLinecap="round" strokeLinejoin="round" />
      {status !== 'sent' && (
        <path d="M7.6 7.8 10.4 11 17.6 3" stroke="currentColor" strokeWidth="1.7"
          strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  )
}

export default function ChatRoom () {
  const { chatId } = useParams()
  const { user } = useAuth()
  const { chats } = useChats()
  const { messages, isLoading, send } = useMessages(chatId)
  const [draft, setDraft] = useState('')
  const [emojiOpen, setEmojiOpen] = useState(false)
  const [phoneTapped, setPhoneTapped] = useState(null)
  /** The photo open in the full-screen viewer, or null. */
  const [viewing, setViewing] = useState(null)
  const [cameraOpen, setCameraOpen] = useState(false)
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [sending, setSending] = useState(false)
  const [attachError, setAttachError] = useState(null)

  const endRef = useRef(null)
  const inputRef = useRef(null)
  const fileRef = useRef(null)
  const cameraRef = useRef(null)
  const recorderRef = useRef(null)
  const discardRef = useRef(false)

  const chat = chats.find(c => c.id === chatId)

  // Follow the conversation as it grows. Instant on first paint so you do not
  // watch it scroll, smooth afterwards so a new message reads as arriving.
  const firstPaint = useRef(true)
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: firstPaint.current ? 'auto' : 'smooth' })
    firstPaint.current = false
  }, [messages.length, chatId])

  useEffect(() => {
    firstPaint.current = true
    inputRef.current?.focus()
  }, [chatId])

  /*
   * The running timer.
   *
   * Driven from an effect rather than from inside the recorder, so it
   * is torn down with the component — a chat closed mid-recording must
   * not leave an interval setting state on something unmounted.
   */
  useEffect(() => {
    if (!recording) return undefined
    const started = Date.now()
    const tick = setInterval(() => setElapsed(Date.now() - started), 200)
    return () => clearInterval(tick)
  }, [recording])

  async function onSubmit (event) {
    event.preventDefault()
    const body = draft.trim()
    if (!body) return
    setDraft('')
    await send({ type: 'text', body })
  }

  /**
   * Attach a file, from the picker or the camera.
   *
   * The kind is decided from the MIME type rather than asked for: an
   * image is shown inline, everything else is a document with its name.
   * The caption, if one was typed, rides along rather than being lost.
   */
  /** Upload and send a File, from wherever it came. */
  async function sendCapturedFile (file) {
    if (!file || sending) return
    if (!isWithinLimit(file)) {
      const kind = kindOf(file)
      setAttachError(
        `That ${kind} is ${formatSize(file.size)}. ` +
        `The limit is ${formatSize(MAX_UPLOAD_BYTES[kind])}.`
      )
      return
    }

    setSending(true)
    setAttachError(null)
    try {
      const uploaded = await media.upload(file)
      const kind = kindOf(file)
      const caption = draft.trim()
      setDraft('')

      await send({
        type: kind === 'image' ? 'image' : kind === 'video' ? 'video' : 'document',
        body: caption || (kind === 'image' || kind === 'video' ? null : file.name),
        mediaUrl: uploaded.url,
        mediaMeta: { name: file.name, size: file.size, mimeType: file.type }
      })
    } catch (error) {
      setAttachError(userMessage(error))
    } finally {
      setSending(false)
    }
  }

  async function sendFile (event) {
    const file = event.target.files?.[0]
    // Cleared immediately so choosing the same file twice fires again.
    event.target.value = ''
    // One upload path, shared with the camera — the two used to be
    // separate copies, and only one of them had the size check fixed.
    return sendCapturedFile(file)
  }

  /**
   * Voice notes, through MediaRecorder.
   *
   * Press to start, press again to send — rather than hold-to-talk,
   * which needs pointer capture and gets lost the moment the pointer
   * leaves the button. The browser asks for the microphone the first
   * time; refusing it says so instead of failing silently.
   */
  async function startRecording () {
    if (recording) return
    setAttachError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks = []

      recorder.ondataavailable = e => { if (e.data.size) chunks.push(e.data) }
      // Measured here, not read back off the file. MediaRecorder writes
      // WebM with no duration in it, so the <audio> element reports
      // Infinity until it is seeked and the player showed 0:00 forever.
      const startedAt = Date.now()

      recorder.onstop = async () => {
        const durationMs = Date.now() - startedAt
        // Released as soon as recording ends, or the browser keeps
        // showing the microphone as live for the rest of the session.
        for (const track of stream.getTracks()) track.stop()

        // Cancelled: stop here, the microphone already released. Without
        // this, Cancel would stop the recorder and then cheerfully send
        // everything it had captured.
        if (discardRef.current) { discardRef.current = false; return }

        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' })
        if (blob.size === 0) return

        setSending(true)
        try {
          const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type })
          const uploaded = await media.upload(file)
          await send({
            type: 'audio',
            body: null,
            mediaUrl: uploaded.url,
            mediaMeta: {
              name: file.name, size: file.size, mimeType: file.type,
              voice: true, durationMs
            }
          })
        } catch (error) {
          setAttachError(userMessage(error))
        } finally {
          setSending(false)
        }
      }

      recorder.start()
      recorderRef.current = recorder
      discardRef.current = false
      setElapsed(0)
      setRecording(true)
    } catch {
      setAttachError('ZIvapp needs permission to use your microphone to record a voice note.')
    }
  }

  /** mm:ss, because a voice note is never hours long. */
  function formatElapsed (ms) {
    const total = Math.floor(ms / 1000)
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
  }

  /**
   * Throw the recording away.
   *
   * The flag is read by onstop, which is the only place that knows the
   * blob — without it, cancelling would stop the recorder and then
   * cheerfully upload what it had.
   */
  function cancelRecording () {
    discardRef.current = true
    recorderRef.current?.stop()
    recorderRef.current = null
    setRecording(false)
  }

  function stopRecording () {
    recorderRef.current?.stop()
    recorderRef.current = null
    setRecording(false)
  }

  return (
    <div className="cr">
      <header className="cr__head">
        <Link className="cr__back" to="/chats" aria-label="Back to chats">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>

        <Avatar name={chat?.title} url={chat?.avatarUrl ?? chat?.peerAvatarUrl} size={40}
          group={chat?.type === 'group'} />

        {/* The subtitle has said "tap for group info" since this screen
            was built; it now does. */}
        <Link
          className="cr__who"
          to={chat?.type === 'group' ? `/groups/${chatId}` : `/chats/${chatId}`}
        >
          <span className="cr__name">{chat?.title ?? 'Chat'}</span>
          <span className="cr__sub">
            {chat?.type === 'group' ? 'Tap for group info' : 'Tap for contact info'}
          </span>
        </Link>

        {/* Video and voice call, then the menu. Calling someone is the
            second thing you do in a conversation after reading it, so the
            reference puts both in the header — search lives in the menu. */}
        <span className="cr__head-actions">
          <button className="cr__icon-btn" type="button" aria-label="Video call">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="2.5" y="6" width="13" height="12" rx="2.6"
                stroke="currentColor" strokeWidth="1.8" />
              <path d="M15.5 11l5-3.2v8.4l-5-3.2Z" stroke="currentColor"
                strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </button>

          <button className="cr__icon-btn" type="button" aria-label="Voice call">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </button>

          <button className="cr__icon-btn" type="button" aria-label="Menu">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="5" r="1.7" fill="currentColor" />
              <circle cx="12" cy="12" r="1.7" fill="currentColor" />
              <circle cx="12" cy="19" r="1.7" fill="currentColor" />
            </svg>
          </button>
        </span>
      </header>

      <div className="cr__thread">
        <div className="cr__wall" aria-hidden="true" />

        <div className="cr__scroll">
          {isLoading && <p className="cr__state">Loading messages…</p>}

          {!isLoading && messages.length === 0 && (
            <p className="cr__state">No messages yet. Say hello.</p>
          )}

          {messages.map((message, i) => {
            const mine = message.senderId === user?.id
            const previous = messages[i - 1]
            const newDay = !previous || !isSameDay(previous.createdAt, message.createdAt)
            // Same person, same day, still mid-run: tighten and drop the tail.
            const run = previous && !newDay && previous.senderId === message.senderId

            return (
              <div key={message.id}>
                {newDay && (
                  <div className="cr__day">
                    <span>{formatDayDivider(message.createdAt)}</span>
                  </div>
                )}

                <div className={[
                  'cr__row',
                  mine ? 'is-mine' : 'is-theirs',
                  run ? 'is-run' : ''
                ].join(' ').trim()}>
                  <div className={[
                    'cr__bubble',
                    // A photo or video with no caption: the picture IS the
                    // bubble, so the time goes on top of it rather than on
                    // a strip of bubble colour underneath.
                    !message.body && !message.deletedAt && message.mediaUrl &&
                      (message.type === 'image' || message.type === 'video')
                      ? 'is-media' : ''
                  ].join(' ').trim()}>
                    {/* The quoted message, when this one is a reply. Looked
                        up in what is already loaded rather than fetched: a
                        reply almost always answers something nearby, and a
                        request per bubble would be absurd. */}
                    {message.replyToId && (() => {
                      const quoted = messages.find(m => m.id === message.replyToId)
                      const fromMe = quoted?.senderId === user?.id
                      return (
                        <span className={`cr__quote${fromMe ? ' is-mine' : ''}`}>
                          <span className="cr__quote-who">
                            {fromMe ? 'You' : (chat?.title ?? quoted?.senderId ?? 'Unknown')}
                          </span>
                          <span className="cr__quote-text">
                            {quoted
                              ? (quoted.deletedAt ? 'This message was deleted' : quoted.body)
                              : 'Original message not available'}
                          </span>
                        </span>
                      )
                    })()}

                    {message.deletedAt ? (
                      <span className="cr__deleted">
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
                          <path d="m5.6 5.6 12.8 12.8" stroke="currentColor" strokeWidth="1.7" />
                        </svg>
                        This message was deleted
                      </span>
                    ) : (
                      <>
                        {/* Photos, videos, files and voice notes. The
                            bubble drew only body before, so every one
                            of these arrived empty. */}
                        <MessageMedia
                          message={message}
                          mine={mine}
                          onOpenImage={m => setViewing({
                            url: m.mediaUrl,
                            who: mine ? 'You' : (chat?.title ?? 'Photo'),
                            when: formatTime(m.createdAt)
                          })}
                        />
                        {message.body && (
                      <span className="cr__text">
                        {/* Phone numbers become tappable. Tapping one
                            asks whether they are on ZIvapp and offers
                            either Message or Invite. */}
                        {segments(message.body).map((part, i) =>
                          typeof part === 'string'
                            ? <span key={i}>{part}</span>
                            : (
                              <button
                                key={i}
                                type="button"
                                className="cr__phone"
                                onClick={() => setPhoneTapped(part.phone)}
                                data-testid="message-phone"
                              >
                                {part.phone}
                              </button>
                            )
                        )}
                          </span>
                        )}
                      </>
                    )}

                    <span className="cr__stamp">
                      {formatTime(message.createdAt)}
                      {mine && !message.deletedAt && <Ticks status={message.status} />}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}

          <div ref={endRef} />
        </div>
      </div>

      {/* Above the composer, in flow — see the note at the top of
          EmojiPicker.css for why it must not float over it. */}
      {emojiOpen && (
        <EmojiPicker
          onPick={emoji => { setDraft(d => d + emoji); inputRef.current?.focus() }}
          onClose={() => setEmojiOpen(false)}
        />
      )}

      {/*
        While recording, this bar REPLACES the composer.

        WhatsApp's own behaviour: a running timer, a way to throw the
        recording away, and a way to send it. Typing is not available
        mid-recording there either, which is why swapping the whole row
        is right rather than squeezing a timer in beside the input.
      */}
      {recording && (
        <div className="cr__recorder">
          <button
            type="button"
            className="cr__rec-cancel"
            onClick={cancelRecording}
            aria-label="Delete this recording"
            data-testid="recorder-cancel"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 7h14M10 7V5.4A1.4 1.4 0 0 1 11.4 4h1.2A1.4 1.4 0 0 1 14 5.4V7M7 7l.8 12.1a1.4 1.4 0 0 0 1.4 1.3h5.6a1.4 1.4 0 0 0 1.4-1.3L17 7"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"
                strokeLinejoin="round" />
            </svg>
          </button>

          <span className="cr__rec-dot" aria-hidden="true" />
          <span className="cr__rec-time" data-testid="recorder-time">
            {formatElapsed(elapsed)}
          </span>
          <span className="cr__rec-hint">Recording…</span>

          <button
            type="button"
            className="cr__send"
            onClick={stopRecording}
            aria-label="Send the voice note"
            data-testid="recorder-send"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M21 3 3 10.6l7 3 3 7Z" stroke="currentColor" strokeWidth="1.8"
                strokeLinejoin="round" />
              <path d="m10 13.6 11-10.6" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        </div>
      )}

      {/* The field is one rounded pill holding emoji, the input, attach and
          camera. Send and record sit outside it as a single filled button
          that swaps once there is something to send. */}
      {!recording && (
      <form className="cr__composer" onSubmit={onSubmit}>
        <div className="cr__field">
          <button
            className="cr__field-btn"
            type="button"
            aria-label="Emoji"
            aria-expanded={emojiOpen}
            onClick={() => setEmojiOpen(open => !open)}
            data-testid="composer-emoji"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 14s1.3 2 3.5 2 3.5-2 3.5-2" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" />
              <path d="M9.2 9.4v1.1M14.8 9.4v1.1" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>

          <input
            ref={inputRef}
            className="cr__input"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Message"
            aria-label="Message"
          />

          <button
            className="cr__field-btn"
            type="button"
            aria-label="Attach a file"
            onClick={() => fileRef.current?.click()}
            data-testid="composer-attach"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M20 11.5 12.3 19a4.6 4.6 0 0 1-6.5-6.5l7.6-7.6a3 3 0 0 1 4.3 4.3l-7.6 7.6a1.5 1.5 0 0 1-2.1-2.1l7-7"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            className="cr__field-btn"
            type="button"
            aria-label="Take a photo"
            onClick={() => {
              const onPhone = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
              if (onPhone) cameraRef.current?.click()
              else setCameraOpen(true)
            }}
            data-testid="composer-camera"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 8h2.8l1.4-2.2h7.6L17.2 8H20a1.8 1.8 0 0 1 1.8 1.8v8.4A1.8 1.8 0 0 1 20 20H4a1.8 1.8 0 0 1-1.8-1.8V9.8A1.8 1.8 0 0 1 4 8Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="13.8" r="3.4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        </div>

        {/* Driven by the buttons above. capture="environment" asks a phone
            for its camera; a desktop ignores it and shows the ordinary
            picker, which is the right fallback rather than an error. */}
        <input
          ref={fileRef}
          className="ziv-sr-only"
          type="file"
          onChange={sendFile}
          data-testid="composer-file"
        />
        <input
          ref={cameraRef}
          className="ziv-sr-only"
          type="file"
          accept="image/*"
          capture="environment"
          onChange={sendFile}
          data-testid="composer-camera-file"
        />

        {draft.trim() ? (
          <button className="cr__send" type="submit" aria-label="Send">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M21 3 3 10.6l7 3 3 7Z" stroke="currentColor" strokeWidth="1.8"
                strokeLinejoin="round" />
              <path d="m10 13.6 11-10.6" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </button>
        ) : (
          <button
            className={`cr__send${recording ? ' is-recording' : ''}`}
            type="button"
            aria-label={recording ? 'Stop and send the voice note' : 'Record a voice note'}
            onClick={recording ? stopRecording : startRecording}
            data-testid="composer-record"
          >
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 15.2a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6.2a3 3 0 0 0 3 3Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M6.4 11.4v.8a5.6 5.6 0 0 0 11.2 0v-.8M12 18.2V21"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </form>
      )}

      {sending && <p className="cr__sending" role="status">Uploading…</p>}

      {attachError && (
        <p className="cr__attach-error" role="alert">{attachError}</p>
      )}

      {cameraOpen && (
        <CameraSheet
          onCapture={file => sendCapturedFile(file)}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {phoneTapped && (
        <PhoneSheet raw={phoneTapped} onClose={() => setPhoneTapped(null)} />
      )}

      <Lightbox
        open={Boolean(viewing)}
        url={viewing?.url}
        who={viewing?.who}
        when={viewing?.when}
        onClose={() => setViewing(null)}
      />
    </div>
  )
}
